import { AccountInfo, BrowserAuthError, InteractionType } from "@azure/msal-browser";
import { useAccount, useMsal } from "@azure/msal-react";
import { useAuthScopes } from "Core/Auth/useAuthScopes";
import { RenderingStatus } from "Enumerations";
import { runInAction } from "mobx";

import { useCallback } from "react";
import { useGlobalSetting } from "Store/GlobalSettingStore";
import { useRootStore } from "Store/RootStore";

// MSAL persists its in-flight interaction state at `msal.interaction.status` in sessionStorage
// (MSAL >= 5.10; older versions wrote `msal.<clientId>.interaction.status` to the configured
// cache location, localStorage here). After a redirect-URI change (Phase 4 moved
// /v2/redirect.html → /redirect.html), any session left over from before the swap will have
// this key stuck — every subsequent call into MSAL throws "interaction_in_progress" until it's
// cleared. A concurrent silent token refresh from useMsalAuthentication can produce the
// same symptom transiently.
const clearStuckInteractionStatus = (): void => {
    [window.sessionStorage, window.localStorage].forEach((storage) =>
        Object.keys(storage)
            .filter((key) => key.endsWith(".interaction.status"))
            .forEach((key) => storage.removeItem(key))
    );
};

const isInteractionInProgressError = (err: unknown): boolean => err instanceof BrowserAuthError && err.errorCode === "interaction_in_progress";

export const useAuth = () => {
    const { instance, accounts } = useMsal();
    const authStore = useRootStore("AuthStore");
    const { lastLoginUser, setLastUserName } = useGlobalSetting("auth");
    const currentAccount = useAccount(lastLoginUser);
    const scopes = useAuthScopes();

    const logout = useCallback(async () => {
        const request = { account: currentAccount };
        try {
            await instance.logoutRedirect(request);
        } catch (err) {
            if (!isInteractionInProgressError(err)) {
                throw err;
            }
            clearStuckInteractionStatus();
            await instance.logoutRedirect(request);
        }
    }, [currentAccount, instance]);

    const logoutAccount = useCallback(
        async (account: AccountInfo) => {
            const request = { account };
            try {
                await instance.logoutPopup(request);
            } catch (err) {
                if (!isInteractionInProgressError(err)) {
                    throw err;
                }
                clearStuckInteractionStatus();
                await instance.logoutPopup(request);
            }
        },
        [instance]
    );

    const loginWithUser = useCallback(
        (username: string | undefined) => {
            const account = accounts.find((acc) => acc.username === username);
            const request = {
                scopes: scopes,
                loginHint: account?.username,
                prompt: account ? undefined : "select_account"
            };
            runInAction(() => {
                setLastUserName(username);
                authStore.readyToCheckRole = false;
                authStore.loginStatus = RenderingStatus.Waiting;
            });
            authStore.login(account ? InteractionType.Silent : InteractionType.Redirect, request);
        },
        [accounts, authStore, scopes, setLastUserName]
    );

    return {
        authError: authStore.error,
        login: authStore.login,
        logout,
        logoutAccount,
        loginWithUser
    };
};
